import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { authorizedFetch } from './utils/api';
import { BACKEND_URL } from './utils/backend';

// Enhanced Look Card Component
function EnhancedLookCard({ look, onBuyerScoreChange, savingScore, onFavorite }) {
  const buyerScore = typeof look.buyer_score === 'number' ? look.buyer_score : 0;
  const machineScore = typeof look.machine_score === 'number' ? look.machine_score : null;
  const imageUrl = look.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
  const [localScore, setLocalScore] = React.useState(buyerScore);
  const [error, setError] = React.useState(null);
  const [isFavorited, setIsFavorited] = React.useState(buyerScore >= 8);
  const [manualScore, setManualScore] = React.useState(buyerScore > 0 ? String(buyerScore) : '');

  const handleScoreChange = async (e) => {
    const newScore = Number(e.target.value);
    setLocalScore(newScore);
    setError(null);
    setIsFavorited(newScore >= 8);
    try {
      await onBuyerScoreChange(look.id, newScore);
    } catch (err) {
      setError('Failed to save score');
    }
  };

  const handleFavoriteClick = () => {
    const newScore = isFavorited ? 7 : 9;
    setLocalScore(newScore);
    setIsFavorited(!isFavorited);
    onBuyerScoreChange(look.id, newScore);
  };

  const handleManualScoreSubmit = async () => {
    const parsed = parseFloat(manualScore);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
      setError('Please enter a valid score between 0 and 10 (decimals allowed).');
      return;
    }
    const normalized = Math.round(parsed * 100) / 100;
    setLocalScore(normalized);
    setError(null);
    setIsFavorited(normalized >= 8);
    try {
      await onBuyerScoreChange(look.id, normalized);
    } catch (err) {
      setError('Failed to save score');
    }
  };

  let lookNumber = '?';
  let lookNumberWarning = false;
  if (typeof look.index === 'number' && look.index !== null) {
    lookNumber = look.index;
  } else {
    lookNumberWarning = true;
  }

  return (
    <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Image with overlay effects */}
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={imageUrl}
          alt={look.designer ? `${look.designer} Look ${lookNumber}` : `Look ${lookNumber}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={e => {
            e.target.onerror = null;
            e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
          }}
        />

        {/* Hover overlay with quick actions */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300">
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleFavoriteClick}
              className={`rounded-full p-2 shadow-lg transition-colors ${isFavorited ? 'bg-red-500 text-white' : 'bg-white text-gray-600 hover:text-red-500'
                }`}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              ❤️
            </button>
          </div>
        </div>

        {/* Score badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          {machineScore && (
            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              🧠 {machineScore}
            </span>
          )}
          {buyerScore > 0 && (
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              👤 {localScore}
            </span>
          )}
        </div>
      </div>

      {/* Enhanced card content */}
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-2 text-gray-900">
          Look {lookNumber}
          {lookNumberWarning && (
            <span className="ml-2 text-xs text-red-500 bg-yellow-100 px-2 py-0.5 rounded">
              No index
            </span>
          )}
        </h3>

        <div className="text-sm text-gray-600 space-y-1 mb-3">
          <div className="flex items-center">
            <span className="mr-2">👔</span>
            <span>{look.brand || 'Unknown Brand'}</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">✨</span>
            <span>{look.designer || 'Unknown Designer'}</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">📅</span>
            <span>{look.season || 'Unknown Season'} {look.year || ''}</span>
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-medium">Description:</span>{' '}
            {look.description && String(look.description).trim() ? look.description : 'N/A'}
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-medium">AI Auxiliary Buying Decision:</span>{' '}
            {look.ai_auxiliary_buying_decision && String(look.ai_auxiliary_buying_decision).trim()
              ? look.ai_auxiliary_buying_decision
              : 'N/A'}
          </div>
        </div>

        {/* Score selector - Collections-style circular buttons */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-green-600 font-semibold">👤 Buyer Score</span>
            <span className={`text-sm font-bold ${localScore >= 8 ? 'text-green-600' :
              localScore >= 6 ? 'text-yellow-600' :
                localScore > 0 ? 'text-red-600' : 'text-gray-400'
              }`}>
              {localScore || 'Not rated'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1 justify-center">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
              <button
                key={score}
                onClick={() => {
                  const newScore = score;
                  setLocalScore(newScore);
                  setError(null);
                  setIsFavorited(newScore >= 8);
                  onBuyerScoreChange(look.id, newScore);
                }}
                disabled={savingScore}
                className={`w-7 h-7 rounded-full text-xs font-medium transition-all ${localScore === score
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  } disabled:opacity-50`}
              >
                {score}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={manualScore}
              onChange={(e) => setManualScore(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleManualScoreSubmit();
                }
              }}
              placeholder="e.g. 7.5"
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
              disabled={savingScore}
            />
            <button
              type="button"
              onClick={handleManualScoreSubmit}
              disabled={savingScore}
              className="px-2 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
            >
              Save
            </button>
          </div>
          {savingScore && <div className="text-xs text-gray-400 text-center">Saving...</div>}
        </div>

        {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
      </div>
    </div>
  );
}

EnhancedLookCard.propTypes = {
  look: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    index: PropTypes.number,
    brand: PropTypes.string,
    season: PropTypes.string,
    designer: PropTypes.string,
    image_url: PropTypes.string,
    description: PropTypes.string,
    ai_auxiliary_buying_decision: PropTypes.string,
    machine_score: PropTypes.number,
    buyer_score: PropTypes.number,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onBuyerScoreChange: PropTypes.func.isRequired,
  savingScore: PropTypes.bool,
  onFavorite: PropTypes.func,
};

// Collection Header Component
function CollectionHeader({ metadata }) {
  const { brand, designer, season, year, look_count, avg_score, high_rated, unrated } = metadata;

  return (
    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 sm:p-6 rounded-lg mb-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{brand || 'Unknown Brand'}</h1>
          <p className="text-lg opacity-90">
            {designer || 'Unknown Designer'} • {season || 'Unknown Season'} {year || ''}
          </p>
          <p className="text-sm opacity-75">
            Latest Collection • {look_count || 0} looks
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold">{avg_score || 0}/10</div>
          <div className="text-sm opacity-75">Avg Score</div>
        </div>
      </div>
    </div>
  );
}

// Collection Statistics Component
function CollectionStats({ metadata }) {
  const { look_count, avg_score, high_rated, unrated } = metadata;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-2xl font-bold text-blue-600">{look_count || 0}</div>
        <div className="text-sm text-gray-600">Total Looks</div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-2xl font-bold text-green-600">{avg_score || 0}</div>
        <div className="text-sm text-gray-600">Avg Score</div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-2xl font-bold text-purple-600">{high_rated || 0}</div>
        <div className="text-sm text-gray-600">High Rated (8+)</div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-2xl font-bold text-orange-600">{unrated || 0}</div>
        <div className="text-sm text-gray-600">Unrated</div>
      </div>
    </div>
  );
}

function detectReviewLanguage(reviewText = '') {
  return /[\u4e00-\u9fff]/.test(reviewText) ? '中文' : 'English';
}

function CollectionReview({ review }) {
  if (!review) return null;

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-4 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">WWD Collection Review</h3>
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
          {detectReviewLanguage(review)}
        </span>
      </div>
      <p className="text-sm leading-6 text-gray-700 whitespace-pre-line">
        {review}
      </p>
    </div>
  );
}

// Collection Carousel Component
function CollectionCarousel({ collections, currentCollectionId, onCollectionSelect }) {
  return (
    <aside className="bg-white p-3 rounded-lg shadow border border-gray-200 lg:sticky lg:top-4">
      <h3 className="text-lg font-semibold mb-3">Recent Collections</h3>
      {collections && collections.length > 0 ? (
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          {collections.map((collection) => (
            <button
              key={collection.id}
              type="button"
              onClick={() => onCollectionSelect(collection.id)}
              className={`w-full text-left p-3 rounded-lg transition-colors border ${collection.id === currentCollectionId
                ? 'bg-blue-50 border-blue-400'
                : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
            >
              <div className="font-medium text-sm text-gray-900">{collection.brand}</div>
              <div className="text-xs text-gray-600">{collection.designer}</div>
              <div className="text-xs text-gray-500">{collection.season} {collection.year || ''}</div>
              <div className="text-xs text-blue-600">{collection.look_count} looks</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500">No recent collections.</div>
      )}
    </aside>
  );
}

// View Mode Toggle Component
function ViewModeToggle({ viewMode, onViewModeChange }) {
  return (
    <div className="flex items-center gap-4 mb-4">
      <div className="flex bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => onViewModeChange('grid')}
          className={`px-3 py-1 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow' : ''
            }`}
        >
          Grid View
        </button>
        <button
          onClick={() => onViewModeChange('masonry')}
          className={`px-3 py-1 rounded text-sm ${viewMode === 'masonry' ? 'bg-white shadow' : ''
            }`}
        >
          Masonry
        </button>
      </div>
    </div>
  );
}

// Loading Skeleton Component
function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-8 bg-gray-300 rounded mb-4"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="aspect-[2/3] bg-gray-300"></div>
            <div className="p-4">
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-1"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Main LatestLooks Component
export default function LatestLooks({ page = 1, itemsPerPage = 12, onDataLoaded, onCollectionChange }) {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collectionMetadata, setCollectionMetadata] = useState({});
  const [recentCollections, setRecentCollections] = useState([]);
  const [currentCollectionId, setCurrentCollectionId] = useState(null);
  const [savingScoreId, setSavingScoreId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [batchScore, setBatchScore] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchMessage, setBatchMessage] = useState('');
  const [batchError, setBatchError] = useState('');
  const [renderTime, setRenderTime] = useState(null);
  const [showRenderTime, setShowRenderTime] = useState(false);
  const renderStartRef = React.useRef(null);

  // /fashion-looks/* lives on the Fashion Buyer API. This read REACT_APP_BACKEND2_BASE_URL,
  // which is the Virtual Try-On API, so every request went to the wrong service and the
  // page showed 'Error Loading Collection / Failed to fetch'.
  const BACKEND2_BASE_URL = BACKEND_URL;

  const fetchLatestCollection = async () => {
    renderStartRef.current = performance.now();
    setLoading(true);
    setError(null);

    try {
      const url = `${BACKEND2_BASE_URL}/fashion-looks/latest-collection`;
      const response = await authorizedFetch(url);

      if (!response.ok) throw new Error('Failed to fetch latest collection');

      const data = await response.json();
      const { metadata, looks: looksArr } = data;

      // Sort looks by index
      const sortedLooks = Array.isArray(looksArr) ? [...looksArr].sort((a, b) => {
        if (typeof a.index === 'number' && typeof b.index === 'number') {
          return a.index - b.index;
        }
        return 0;
      }) : [];

      // Call onDataLoaded callback with the full array for pagination
      if (onDataLoaded && typeof onDataLoaded === 'function') {
        onDataLoaded(sortedLooks);
      }

      setLooks(sortedLooks);
      setCollectionMetadata(metadata || {});
      setCurrentCollectionId(metadata?.collection_id);

      setLoading(false);
      const end = performance.now();
      const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
      setRenderTime(seconds);
      setShowRenderTime(true);
      setTimeout(() => setShowRenderTime(false), 5000);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchRecentCollections = async () => {
    try {
      const url = `${BACKEND2_BASE_URL}/fashion-looks/recent-collections?limit=500`;
      const response = await authorizedFetch(url);

      if (response.ok) {
        const collections = await response.json();
        setRecentCollections(collections);
      } else {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error(`Failed to fetch recent collections: ${response.status} ${response.statusText}`);
        }
        // Set empty array to prevent errors in the UI
        setRecentCollections([]);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch recent collections:', err);
      }
      // Set empty array to prevent errors in the UI
      setRecentCollections([]);
    }
  };

  const fetchCollectionById = async (collectionId) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${BACKEND2_BASE_URL}/fashion-looks/collection/${collectionId}`;
      const response = await authorizedFetch(url);

      if (!response.ok) throw new Error('Failed to fetch collection');

      const data = await response.json();
      const { metadata, looks: looksArr } = data;

      // Sort looks by index
      const sortedLooks = Array.isArray(looksArr) ? [...looksArr].sort((a, b) => {
        if (typeof a.index === 'number' && typeof b.index === 'number') {
          return a.index - b.index;
        }
        return 0;
      }) : [];

      // Call onDataLoaded callback with the full array for pagination
      if (onDataLoaded && typeof onDataLoaded === 'function') {
        onDataLoaded(sortedLooks);
      }

      setLooks(sortedLooks);
      setCollectionMetadata(metadata || {});
      setCurrentCollectionId(collectionId);
      setLoading(false);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestCollection();
    fetchRecentCollections();
  }, []); // Only run on initial mount

  const onBuyerScoreChange = async (lookId, score) => {
    setSavingScoreId(lookId);
    try {
      const resp = await authorizedFetch('/fashion-looks/buyer-score', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ look_id: lookId, buyer_score: score })
      });
      if (!resp.ok) throw new Error('Failed to save');

      // Update local state
      setLooks(looks => looks.map(l =>
        l.id === lookId ? { ...l, buyer_score: score } : l
      ));

      // Update metadata stats
      const updatedLooks = looks.map(l =>
        l.id === lookId ? { ...l, buyer_score: score } : l
      );
      const scoredLooks = updatedLooks.filter(l => l.buyer_score && l.buyer_score > 0);
      const avgScore = scoredLooks.length > 0
        ? Math.round((scoredLooks.reduce((sum, l) => sum + l.buyer_score, 0) / scoredLooks.length) * 10) / 10
        : 0;
      const highRated = updatedLooks.filter(l => l.buyer_score && l.buyer_score >= 8).length;
      const unrated = updatedLooks.filter(l => !l.buyer_score || l.buyer_score === 0).length;

      setCollectionMetadata(prev => ({
        ...prev,
        avg_score: avgScore,
        high_rated: highRated,
        unrated: unrated
      }));

    } finally {
      setSavingScoreId(null);
    }
  };

  const applyBatchScoreToTenLooks = async () => {
    const parsed = parseFloat(batchScore);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
      setBatchError('Please enter a valid score between 0 and 10.');
      setBatchMessage('');
      return;
    }

    const normalizedScore = Math.round(parsed * 100) / 100;
    const visibleLooks = looks.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const targets = visibleLooks.slice(0, 10);
    if (targets.length === 0) {
      setBatchError('No looks available on this page.');
      setBatchMessage('');
      return;
    }

    setBatchSaving(true);
    setBatchError('');
    setBatchMessage('');
    try {
      const results = await Promise.allSettled(
        targets.map((look) =>
          authorizedFetch('/fashion-looks/buyer-score', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ look_id: look.id, buyer_score: normalizedScore })
          })
        )
      );

      const successfulIds = [];
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled' && res.value?.ok) {
          successfulIds.push(targets[idx].id);
        }
      });

      if (successfulIds.length > 0) {
        setLooks((prevLooks) => {
          const idSet = new Set(successfulIds);
          const updatedLooks = prevLooks.map((look) =>
            idSet.has(look.id) ? { ...look, buyer_score: normalizedScore } : look
          );

          const scoredLooks = updatedLooks.filter((l) => l.buyer_score && l.buyer_score > 0);
          const avgScore = scoredLooks.length > 0
            ? Math.round((scoredLooks.reduce((sum, l) => sum + l.buyer_score, 0) / scoredLooks.length) * 10) / 10
            : 0;
          const highRated = updatedLooks.filter((l) => l.buyer_score && l.buyer_score >= 8).length;
          const unrated = updatedLooks.filter((l) => !l.buyer_score || l.buyer_score === 0).length;

          setCollectionMetadata((prev) => ({
            ...prev,
            avg_score: avgScore,
            high_rated: highRated,
            unrated: unrated
          }));

          return updatedLooks;
        });
      }

      const failedCount = targets.length - successfulIds.length;
      if (successfulIds.length > 0) {
        setBatchMessage(
          failedCount > 0
            ? `Scored ${successfulIds.length} looks. ${failedCount} failed.`
            : `Successfully scored ${successfulIds.length} looks.`
        );
      } else {
        setBatchError('Batch scoring failed for all selected looks.');
      }
    } catch (err) {
      setBatchError('Batch scoring failed. Please try again.');
    } finally {
      setBatchSaving(false);
    }
  };

  const handleCollectionSelect = (collectionId) => {
    if (collectionId !== currentCollectionId) {
      // Reset pagination when changing collections
      if (onCollectionChange && typeof onCollectionChange === 'function') {
        onCollectionChange();
      }
      fetchCollectionById(collectionId);
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return (
    <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg">
      <h2 className="text-lg font-semibold mb-2">Error Loading Collection</h2>
      <p>{error}</p>
      <button
        onClick={fetchLatestCollection}
        className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  const gridClass = viewMode === 'masonry'
    ? 'columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-4 space-y-4'
    : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4';
  const pagedLooks = looks.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  return (
    <div className="p-2 animate-fade-in bg-gray-50 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
        <CollectionCarousel
          collections={recentCollections}
          currentCollectionId={currentCollectionId}
          onCollectionSelect={handleCollectionSelect}
        />

        <div>
          <CollectionHeader metadata={collectionMetadata} />

          <CollectionStats metadata={collectionMetadata} />

          <CollectionReview review={collectionMetadata?.review} />

          <ViewModeToggle
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          <div className="mb-4 bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                Batch Score (10 looks on this page)
              </span>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={batchScore}
                onChange={(e) => setBatchScore(e.target.value)}
                placeholder="e.g. 7.5"
                className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                disabled={batchSaving}
              />
              <button
                type="button"
                onClick={applyBatchScoreToTenLooks}
                disabled={batchSaving}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {batchSaving ? 'Scoring...' : 'Apply to 10 Looks'}
              </button>
            </div>
            {batchError && <div className="mt-2 text-xs text-red-500">{batchError}</div>}
            {batchMessage && <div className="mt-2 text-xs text-green-600">{batchMessage}</div>}
          </div>

          {looks.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <div className="text-6xl mb-4">👗</div>
              <h3 className="text-lg font-medium mb-2">No looks found</h3>
              <p className="text-sm">This collection appears to be empty.</p>
            </div>
          ) : (
            <div className={gridClass}>
              {pagedLooks.map((look) => (
                <EnhancedLookCard
                  key={look.id || look.image_url || look.index}
                  look={look}
                  onBuyerScoreChange={onBuyerScoreChange}
                  savingScore={savingScoreId === look.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showRenderTime && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }} className="bg-gray-800 text-white text-xs px-3 py-1 rounded shadow animate-fade-in">
          Render Time: {renderTime} seconds
        </div>
      )}
    </div>
  );
}
